import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Heart, Plus } from "lucide-react";
import ItemImage from "./ItemImage";
import QtyStepper from "./QtyStepper";
import VariationModal from "./VariationModal";
import { formatMoney, lineKey, toCents, useCart } from "../../context/CartContext";

export default function ItemCard({ item, isFavourite = false, onToggleFavourite }) {
  const { getQty, getItemQty, addItem, increment, decrement } = useCart();
  const [optionsOpen, setOptionsOpen] = useState(false);
  // Items with 2+ priced options (Roll / Wedge / Hero): the structured
  // `variations` array is the source of truth, in the merchant's order.
  const variations = (Array.isArray(item.variations) ? item.variations : []).filter(
    (v) => v && String(v.label ?? "").trim() && toCents(v.price) > 0,
  );
  const hasVariations = variations.length >= 2;
  const qty = hasVariations ? 0 : getQty(item);
  const inCartQty = hasVariations ? getItemQty(item) : 0;
  const priceCents = toCents(item.price);
  const purchasable = hasVariations || priceCents > 0;
  const key = lineKey(item);
  // Uploaded inventory data (Import Data page): sale price and category, when set.
  const discountCents = item.discountPrice != null ? toCents(item.discountPrice) : null;
  const hasDiscount = discountCents != null && discountCents > 0 && discountCents < priceCents;

  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition hover:shadow-md">
      <div className="relative aspect-square w-full border-b border-gray-100 bg-white p-3">
        {item.video_url ? (
          // Merchant uploaded a video for this item (View Items page).
          <video
            src={item.video_url}
            className="h-full w-full rounded-lg bg-black object-contain"
            controls
            muted
            playsInline
            preload="metadata"
            aria-label={item.description}
          />
        ) : (
          <ItemImage urls={item.image_urls || [item.image_url]} alt={item.description} className="h-full w-full rounded-lg" />
        )}
        {onToggleFavourite ? (
          <button
            type="button"
            onClick={() => onToggleFavourite(item)}
            aria-pressed={isFavourite}
            aria-label={isFavourite ? `Remove ${item.description} from favourites` : `Add ${item.description} to favourites`}
            title={isFavourite ? "Remove from favourites" : "Add to favourites"}
            className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 shadow-sm transition hover:bg-white"
          >
            <Heart
              className={`h-4 w-4 ${isFavourite ? "fill-rose-500 text-rose-500" : "text-gray-400"}`}
            />
          </button>
        ) : (
          // No toggle for this item (e.g. an AI Menu pick — that's the
          // merchant's favourite, not the shopper's, so it can't be
          // un-favourited here). Still show the red heart as a plain
          // indicator when it is one.
          isFavourite && (
            <span
              aria-label={`${item.description} is one of the store's favourites`}
              title="Store favourite"
              className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 shadow-sm"
            >
              <Heart className="h-4 w-4 fill-rose-500 text-rose-500" />
            </span>
          )
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        {item.category && (
          <span className="w-fit rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
            {item.category}
          </span>
        )}
        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-medium leading-5 text-gray-900" title={item.description}>
          {item.description}
        </h3>

        <div className={`mt-auto flex items-center justify-between gap-2 pt-1 ${hasVariations ? "flex-wrap" : ""}`}>
          {hasVariations ? (
            <span className="text-base font-semibold text-gray-900">
              {variations.map((v) => formatMoney(toCents(v.price))).join(" / ")}
            </span>
          ) : purchasable ? (
            hasDiscount ? (
              <span className="flex items-baseline gap-1.5">
                <span className="text-base font-semibold text-rose-600">{formatMoney(discountCents)}</span>
                <span className="text-xs text-gray-400 line-through">{formatMoney(priceCents)}</span>
              </span>
            ) : (
              <span className="text-base font-semibold text-gray-900">{formatMoney(priceCents)}</span>
            )
          ) : item.priceLabel ? (
            <span className="text-sm font-medium text-gray-700">{item.priceLabel}</span>
          ) : (
            <span className="text-xs font-medium text-gray-400">Price unavailable</span>
          )}

          {hasVariations ? (
            <button
              type="button"
              onClick={() => setOptionsOpen(true)}
              className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              Choose options{inCartQty > 0 ? ` (${inCartQty})` : ""}
            </button>
          ) : qty > 0 ? (
            <QtyStepper size="sm" qty={qty} label={item.description} onDecrement={() => decrement(key)} onIncrement={() => increment(key)} />
          ) : (
            <button
              type="button"
              onClick={() => addItem(item)}
              disabled={!purchasable}
              className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          )}
        </div>
      </div>
      {optionsOpen &&
        // Portal so the card's overflow-hidden can never clip the dialog.
        createPortal(
          <VariationModal
            key={key}
            item={{ ...item, variations }}
            onClose={() => setOptionsOpen(false)}
            onAdd={(selections) => {
              // One cart line per chosen option.
              selections.forEach(({ variation, qty: addQty }) => addItem(item, { variation, qty: addQty }));
              setOptionsOpen(false);
            }}
          />,
          document.body,
        )}
    </article>
  );
}
